import React from 'react';
import { ResumoProgramacao, S } from './liraaApi';
import { BOTAO_CLARO, C, CARTAO, fmtMil } from './LiraaAreaCard';

// LIRAa — Ciclo e Equipe, Aba 2 "Resumo e PDF" (Sessao 30, bloco N10, 24/09/2026 - Walber).
// So mostra o GET /resumo-programacao do motor. Avisos nunca travam nada.

const TH: React.CSSProperties = { ...S.th, whiteSpace: 'normal', lineHeight: 1.2 };
const TD: React.CSSProperties = { ...S.td, verticalAlign: 'top', whiteSpace: 'normal' };
const nQuart = (txt: string | null) => (txt || '').split(',').map(x => x.trim()).filter(Boolean).length;

export default function LiraaResumoAba({ resumo, onResolver }: { resumo: ResumoProgramacao; onResolver: (areaId: number) => void }) {
  const t = resumo.totais;
  const comAviso = resumo.por_area.filter(a => a.avisos.length > 0);
  const nAvisos = comAviso.reduce((s, a) => s + a.avisos.length, 0);

  const grande = (rotulo: string, valor: number | null, destaque = false) => (
    <div style={{ ...CARTAO, marginBottom: 0, padding: '14px 16px', background: destaque ? C.teal : '#ffffff', borderColor: destaque ? C.teal : C.borda }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: destaque ? '#E0F2F7' : C.sub }}>{rotulo}</div>
      <div style={{ fontSize: 30, fontWeight: 900, color: destaque ? '#ffffff' : C.texto, lineHeight: 1.2 }}>{fmtMil(valor)}</div>
    </div>
  );

  return (
    <div>
      {/* C1 — numeros grandes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 12 }}>
        {grande('Áreas no LIRAa', t.areas)}
        {grande('Quarteirões sorteados', t.quarteiroes_sorteados)}
        {grande('Imóveis nos sorteados', t.imoveis)}
        {grande('Imóveis programados', t.programados, true)}
      </div>

      {/* C2 — o que falta */}
      {nAvisos === 0 ? (
        <div style={{ ...CARTAO, padding: '12px 16px', background: C.verdeF, borderColor: '#A7E3BA', color: C.verdeT, fontWeight: 800 }}>
          ✓ Nada pendente na programação.</div>
      ) : (
        <div style={{ ...CARTAO, padding: '12px 16px', background: C.ambarF, borderColor: '#F5D48A', color: C.ambarT }}>
          <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>O que falta ({nAvisos} {nAvisos === 1 ? 'item' : 'itens'}) — não impede nada</div>
          {comAviso.map(a => a.avisos.map((av, i) => (
            <div key={`${a.area_id}-${i}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid #F5D48A' }}>
              <b style={{ minWidth: 130 }}>{i === 0 ? a.area : ''}</b>
              <span style={{ flex: '1 1 260px', fontSize: 14 }}>{av.startsWith(`${a.area}: `) ? av.slice(a.area.length + 2) : av}</span>
              <button type="button" style={{ ...BOTAO_CLARO, borderColor: C.ambarT, color: C.ambarT, background: '#FFFBEB' }} onClick={() => onResolver(a.area_id)}>Resolver →</button>
            </div>
          )))}
        </div>
      )}

      {/* C3 — por agente */}
      <div style={{ fontWeight: 900, fontSize: 16, color: C.texto, margin: '16px 0 8px' }}>Por agente</div>
      {resumo.por_agente.length === 0 && <div style={{ color: C.sub2, fontSize: 14, marginBottom: 12 }}>Ninguém na equipe ainda.</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginBottom: 12 }}>
        {resumo.por_agente.map(g => {
          const faltando = g.areas.filter(a => a.imoveis === null).map(a => a.area);
          return (
            <div key={g.agente_id} style={{ ...CARTAO, marginBottom: 0, padding: '14px 16px' }}>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.texto }}>{g.nome}</div>
              <div style={{ fontSize: 13, color: C.sub2, marginBottom: 8 }}>{g.areas.length} {g.areas.length === 1 ? 'área' : 'áreas'}</div>
              <div style={{ display: 'flex', gap: 18, marginBottom: 8 }}>
                <div><div style={{ fontSize: 12, color: C.sub }}>Imóveis</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: C.texto }}>{g.incompleto ? '—' : fmtMil(g.total_imoveis)}</div></div>
                <div><div style={{ fontSize: 12, color: C.sub }}>Programados</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: C.teal }}>{g.incompleto ? '—' : fmtMil(g.total_programados)}</div></div>
              </div>
              {g.areas.map(a => (
                <div key={a.area_id} style={{ display: 'flex', gap: 6, fontSize: 14, color: C.sub, padding: '3px 0', borderTop: `1px dotted ${C.borda}` }}>
                  <span>{a.area} · {nQuart(a.quarteiroes)} quart.</span>
                  <span style={{ flex: 1, borderBottom: `1px dotted ${C.borda}`, margin: '0 4px 5px' }} />
                  <span style={{ fontWeight: 800, color: C.texto }}>{fmtMil(a.programados)}</span>
                </div>
              ))}
              {faltando.length > 0 && <div style={{ marginTop: 6, fontSize: 13, color: C.ambarT, fontStyle: 'italic' }}>falta o nº de imóveis em {faltando.join(', ')}</div>}
            </div>
          );
        })}
      </div>

      {/* C4 — por area */}
      <div style={{ fontWeight: 900, fontSize: 16, color: C.texto, margin: '16px 0 8px' }}>Por área</div>
      <div style={{ ...CARTAO, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
          <thead><tr>{['Área', 'Quarteirões sorteados', 'Imóveis', 'Program.', 'Inspecion.', 'Agentes'].map(h => <th key={h} style={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {resumo.por_area.map(a => (
              <tr key={a.area_id}>
                <td style={{ ...TD, textAlign: 'left', fontWeight: 800 }}>{a.area}</td>
                <td style={{ ...TD, textAlign: 'left' }}>{a.sorteados || '—'}</td>
                <td style={TD}>{fmtMil(a.imoveis)}</td>
                <td style={TD}>{fmtMil(a.programados)}</td>
                <td style={TD}>{fmtMil(a.inspecionados)}</td>
                <td style={{ ...TD, textAlign: 'left' }}>{a.agentes.length ? a.agentes.map(g => g.nome).join(', ')
                  : <span style={{ fontStyle: 'italic', color: C.ambarT }}>sem agente</span>}</td>
              </tr>
            ))}
            <tr style={{ background: C.cinzaF }}>
              <td style={{ ...TD, textAlign: 'left', fontWeight: 900 }}>TOTAL</td>
              <td style={{ ...TD, textAlign: 'left', fontWeight: 900 }}>{fmtMil(t.quarteiroes_sorteados)} quarteirões</td>
              <td style={{ ...TD, fontWeight: 900 }}>{fmtMil(t.imoveis)}</td>
              <td style={{ ...TD, fontWeight: 900 }}>{fmtMil(t.programados)}</td>
              <td style={{ ...TD, fontWeight: 900 }}>{fmtMil(t.inspecionados)}</td>
              <td style={TD} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
